// Space Designer (Phase 36) — the ported SDS floor planner, wired to the CRM.
// Replaces SDS's ProjectGate with a CRM floor picker; mounts Toolbar/Library/
// Canvas/Inspector; syncs to the backend via useFloorDesignSync; and shows a live
// occupancy panel driven by the projected, bookable resources.
import { useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "wouter";
import {
  useListSpaceFloors,
  getListSpaceFloorsQueryKey,
  useCreateSpaceFloor,
  useGetSpaceOccupancy,
  getGetSpaceOccupancyQueryKey,
  useListSpaces,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Plus, Users, Loader2, CircleDot, CheckCircle2, AlertCircle } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbLink,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { toast } from "sonner";
import { Toolbar } from "./components/Toolbar";
import { Library } from "./components/Library";
import { Canvas } from "./components/Canvas";
import { Inspector } from "./components/Inspector";
import { useFloorDesignSync } from "./lib/floor-sync";
import { diffScenes } from "./lib/scene-diff";
import { usePlanner } from "./lib/store";

export default function SpaceDesignerPage() {
  const params = useParams();
  const routeFloorId = params.floorId ? Number(params.floorId) : null;
  const qc = useQueryClient();
  const [, navigate] = useLocation();

  const { data: floors = [], isLoading: floorsLoading } = useListSpaceFloors({
    query: { queryKey: getListSpaceFloorsQueryKey() },
  });
  const createFloor = useCreateSpaceFloor();
  // Phase 54 Plan 06 (SPACE-UX-BREADCRUMB-01): resolve the current floor's
  // center name client-side — there's no GET /spaces/:id endpoint, so the
  // full list is fetched and matched by id (same pattern as space-hub.tsx).
  const { data: spaces = [] } = useListSpaces();

  const [selectedFloorId, setSelectedFloorId] = useState<number | null>(routeFloorId);
  // Default to the route floor, else the first available floor.
  useEffect(() => {
    if (selectedFloorId != null) return;
    if (routeFloorId != null) { setSelectedFloorId(routeFloorId); return; }
    if (floors.length) setSelectedFloorId(floors[0]!.id);
  }, [floors, routeFloorId, selectedFloorId]);

  const floor = useMemo(() => floors.find((f) => f.id === selectedFloorId) ?? null, [floors, selectedFloorId]);
  const ppf = floor?.pixelsPerFoot ?? 20;
  const centerName = useMemo(
    () => spaces.find((s) => s.id === floor?.spaceId)?.name,
    [spaces, floor?.spaceId],
  );

  const {
    loading: designLoading,
    saving,
    approving,
    discarding,
    isDirty,
    liveScene,
    approve,
    discard,
  } = useFloorDesignSync(selectedFloorId, ppf);

  // Reactively count draft changes vs live so the header pill shows a count.
  const currentScene = usePlanner((s) => s.scene);
  const diff = useMemo(
    () => diffScenes(liveScene, currentScene),
    [liveScene, currentScene],
  );
  const changeCount = diff.totals.new + diff.totals.modified + diff.totals.removed;

  const [showOccupancy, setShowOccupancy] = useState(false);
  const [showConfirmDiscard, setShowConfirmDiscard] = useState(false);

  async function onCreateFloor() {
    const name = window.prompt("Floor name (e.g. Ground Floor)")?.trim();
    if (!name) return;
    const level = floors.length;
    try {
      const created = await createFloor.mutateAsync({ data: { name, level } });
      await qc.invalidateQueries({ queryKey: getListSpaceFloorsQueryKey() });
      setSelectedFloorId((created as { id: number }).id);
      toast.success(`Floor "${name}" created`);
    } catch {
      toast.error("Could not create floor (a floor at this level may already exist)");
    }
  }

  return (
    <div className="flex flex-col h-[calc(100dvh-7rem)] md:h-[calc(100dvh-5rem)] -m-3 sm:-m-4 md:-m-8 bg-background text-foreground">
      {/* Phase 54 Plan 06 (SPACE-UX-BREADCRUMB-01): header breadcrumb linking
          back to the /spaces/:id hub. When floor is undefined (initial load,
          no floors yet), the center-name segment is skipped so the layout
          stays stable — only "Spaces > Designer" renders. */}
      <div className="flex items-center border-b px-3 py-1.5 shrink-0">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink onClick={() => navigate("/spaces")}>Spaces</BreadcrumbLink>
            </BreadcrumbItem>
            {floor != null && (
              <>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink onClick={() => navigate(`/spaces/${floor.spaceId}`)}>
                    {centerName ?? "…"}
                  </BreadcrumbLink>
                </BreadcrumbItem>
              </>
            )}
            <BreadcrumbSeparator />
            <BreadcrumbItem>Designer</BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      {/* Floor picker bar */}
      <div className="flex items-center gap-2 border-b px-3 py-2 shrink-0">
        <span className="text-sm font-medium">Floor</span>
        <Select
          value={selectedFloorId != null ? String(selectedFloorId) : undefined}
          onValueChange={(v) => setSelectedFloorId(Number(v))}
        >
          <SelectTrigger className="h-8 w-56"><SelectValue placeholder={floorsLoading ? "Loading…" : "Select a floor"} /></SelectTrigger>
          <SelectContent>
            {floors.map((f) => (
              <SelectItem key={f.id} value={String(f.id)}>{f.name} · L{f.level}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="h-8 gap-1" onClick={onCreateFloor} disabled={createFloor.isPending}>
          <Plus className="h-4 w-4" /> New floor
        </Button>
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          {(designLoading || saving) && (
            <span className="flex items-center gap-1"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {saving ? "Saving…" : "Loading…"}</span>
          )}

          {/* Draft / Approved status pill */}
          {selectedFloorId != null && !designLoading && (
            isDirty || changeCount > 0 ? (
              <span
                className="flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-amber-900"
                title={`${diff.totals.new} new · ${diff.totals.modified} modified · ${diff.totals.removed} removed`}
              >
                <AlertCircle className="h-3.5 w-3.5" />
                Draft
                {changeCount > 0 && (
                  <span className="ml-0.5 rounded-full bg-amber-200 px-1.5 text-[10px] font-medium tabular-nums">
                    {changeCount}
                  </span>
                )}
              </span>
            ) : (
              <span className="flex items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-emerald-900">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Approved
              </span>
            )
          )}

          {/* Discard draft */}
          {selectedFloorId != null && isDirty && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8"
              onClick={() => setShowConfirmDiscard(true)}
              disabled={discarding}
              title="Throw away the pending draft and restore the last approved design"
            >
              {discarding ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null}
              Discard
            </Button>
          )}

          {/* Approve & publish */}
          {selectedFloorId != null && (
            <Button
              size="sm"
              variant="default"
              className="h-8 gap-1"
              onClick={() => void approve()}
              disabled={!isDirty || approving || saving}
              title={
                !isDirty
                  ? "Nothing to publish — the live design already matches the canvas"
                  : "Publish the draft to /inventory/space and the Add Plan seat picker"
              }
            >
              {approving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Approve &amp; publish
            </Button>
          )}

          <Button size="sm" variant={showOccupancy ? "default" : "ghost"} className="h-8 gap-1" onClick={() => setShowOccupancy((v) => !v)}>
            <Users className="h-4 w-4" /> Occupancy
          </Button>
        </div>
      </div>

      {/* Discard confirmation */}
      {showConfirmDiscard && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setShowConfirmDiscard(false)}
        >
          <div
            className="w-full max-w-sm rounded-md border bg-card p-4 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold">Discard draft?</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              {changeCount} unpublished change{changeCount === 1 ? "" : "s"} will be
              lost. The canvas will snap back to the last approved design.
            </p>
            <div className="mt-3 flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setShowConfirmDiscard(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={async () => {
                  setShowConfirmDiscard(false);
                  await discard();
                }}
              >
                Discard
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Designer */}
      {selectedFloorId == null ? (
        <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
          {floorsLoading ? "Loading floors…" : "Select or create a floor to start designing."}
        </div>
      ) : (
        <>
          <Toolbar />
          <div className="flex flex-1 min-h-0">
            <Library />
            <div className="flex-1 min-w-0 relative">
              <Canvas />
              {showOccupancy && <OccupancyPanel floorId={selectedFloorId} onClose={() => setShowOccupancy(false)} />}
            </div>
            <Inspector />
          </div>
        </>
      )}
    </div>
  );
}

// Live occupancy of the floor's projected bookable resources. Read-only; reflects
// real member assignments so the visual design stays tied to actual bookings.
function OccupancyPanel({ floorId, onClose }: { floorId: number; onClose: () => void }) {
  const { data: rows = [], isLoading } = useGetSpaceOccupancy(
    { floorId },
    { query: { queryKey: getGetSpaceOccupancyQueryKey({ floorId }) } },
  );
  const counts = useMemo(() => {
    let free = 0, occ = 0, oos = 0;
    for (const r of rows) {
      if (r.status === "Occupied") occ++;
      else if (r.status === "Out_of_service") oos++;
      else free++;
    }
    return { free, occ, oos };
  }, [rows]);

  return (
    <div className="absolute top-2 right-2 z-30 w-72 max-h-[80%] overflow-auto rounded-md border bg-card/95 shadow-lg text-xs">
      <div className="sticky top-0 flex items-center justify-between border-b bg-card/95 px-3 py-2">
        <span className="font-medium">Occupancy</span>
        <button className="text-muted-foreground hover:text-foreground" onClick={onClose}>✕</button>
      </div>
      <div className="flex gap-3 px-3 py-2 border-b">
        <span className="text-emerald-600">Free {counts.free}</span>
        <span className="text-rose-600">Occupied {counts.occ}</span>
        <span className="text-muted-foreground">OOS {counts.oos}</span>
      </div>
      {isLoading ? (
        <p className="px-3 py-3 text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="px-3 py-3 text-muted-foreground">No bookable resources yet. Save the design to project seats & cabins.</p>
      ) : (
        <ul className="divide-y">
          {rows.map((r) => (
            <li key={r.resourceId} className="flex items-center gap-2 px-3 py-1.5">
              <CircleDot className={`h-3 w-3 shrink-0 ${r.status === "Occupied" ? "text-rose-600" : r.status === "Out_of_service" ? "text-muted-foreground" : "text-emerald-600"}`} />
              <span className="font-medium truncate">{r.label}</span>
              <span className="ml-auto text-muted-foreground truncate">{r.memberName ?? r.status}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
